import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Navbar, Button, LoadingSpinner, ErrorState } from '../components';
import { profileService, applicationService } from '../services';
import { useUser, useAuth } from '../context';
import { BEHAVIORAL_PROMPTS, ARRIVAL_TIMES, GATHERING_TYPES } from '../constants/partyResumeOptions';

function parseJSON(str) {
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

/**
 * UserProfilePage - Public profile view (full party resume)
 * Displays a user's public profile information looked up by User ID
 */
export default function UserProfilePage() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('applicationId');

  const { profile, walletBalance } = useUser();
  const { logout } = useAuth();

  const [viewedProfile, setViewedProfile] = useState(null);
  const [application, setApplication] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const profileResult = await profileService.getProfileByUserId(userId);
        if (profileResult.success) {
          setViewedProfile(profileResult.data);
        } else {
          setError(profileResult.error || 'Failed to load profile');
          return;
        }

        if (applicationId) {
          const appResult = await applicationService.getApplication(applicationId);
          if (appResult.success) {
            setApplication(appResult.data);
          }
        }
      } catch (err) {
        setError('An unexpected error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      fetchData();
    }
  }, [userId, applicationId]);

  // Parse JSON fields
  const gatheringTypes = parseJSON(viewedProfile?.gatheringTypes) || [];
  const behavioralPrompts = parseJSON(viewedProfile?.behavioralPrompts) || {};
  const extendedProfile = parseJSON(viewedProfile?.extendedProfile) || {};
  const galleryPhotos = viewedProfile?.galleryPhotos || [];

  // Helpers
  const getArrivalLabel = (value) => {
    const match = ARRIVAL_TIMES.find((t) => t.value === value);
    return match ? match.label : value;
  };

  const formatBasicValue = (value) => {
    if (!value || value === 'prefer_not_to_say') return null;
    return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner label="Loading profile..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <ErrorState message={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  // Filter answered behavioral prompts
  const answeredPrompts = BEHAVIORAL_PROMPTS.filter(
    (p) => behavioralPrompts[p.key] && behavioralPrompts[p.key].trim()
  );

  // Check if "The Basics" section has any displayable data
  const genderDisplay = formatBasicValue(viewedProfile?.gender);
  const drinksDisplay = formatBasicValue(viewedProfile?.drinksAlcohol);
  const substancesDisplay = formatBasicValue(viewedProfile?.usesSubstances);
  const hasBasics = genderDisplay || drinksDisplay || substancesDisplay;

  // Extended profile fields
  const contributionTags = extendedProfile.contributionTags || [];
  const likelyTo = extendedProfile.likelyTo || [];
  const signatureContribution = extendedProfile.signatureContribution;
  const musicThatMovesYou = extendedProfile.musicThatMovesYou;
  const arrivalTime = extendedProfile.arrivalTime;
  const hasExtended =
    contributionTags.length > 0 ||
    likelyTo.length > 0 ||
    signatureContribution ||
    musicThatMovesYou ||
    arrivalTime;

  return (
    <div className="min-h-screen bg-white">
      <Navbar
        user={profile}
        walletBalance={walletBalance}
        onLogout={logout}
      />

      {/* Back button */}
      <div className="border-b-2 border-black px-6 py-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          &larr; BACK
        </Button>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        {/* 1. Profile Header */}
        <div className="border-2 border-black bg-white p-8">
          {/* Avatar */}
          <div className="w-48 h-48 mx-auto mb-6 border-2 border-black overflow-hidden flex items-center justify-center bg-gray-100">
            {viewedProfile?.avatarUrl ? (
              <img
                src={viewedProfile.avatarUrl}
                alt={viewedProfile.displayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-mono text-6xl font-bold text-black">
                {viewedProfile?.displayName?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
          </div>

          {/* Display Name */}
          <h1 className="font-mono text-2xl font-bold uppercase tracking-wider text-black text-center mb-2">
            {viewedProfile?.displayName || 'UNKNOWN USER'}
          </h1>

          {/* Handle */}
          {viewedProfile?.handle && (
            <p className="font-mono text-sm text-gray-600 text-center mb-2">
              @{viewedProfile.handle}
            </p>
          )}

          {/* Pronouns + Location */}
          <div className="flex items-center justify-center gap-3 font-mono text-sm text-gray-600">
            {viewedProfile?.pronounsVisible && viewedProfile?.pronouns && (
              <span>{viewedProfile.pronouns}</span>
            )}
            {viewedProfile?.pronounsVisible && viewedProfile?.pronouns && viewedProfile?.location && (
              <span className="text-gray-400">|</span>
            )}
            {viewedProfile?.location && (
              <span>{viewedProfile.location}</span>
            )}
          </div>
        </div>

        {/* 2. Gallery */}
        {galleryPhotos.length > 0 && (
          <div className="border-2 border-black bg-white">
            <div className="border-b-2 border-black px-6 py-3">
              <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                GALLERY
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {galleryPhotos.map((photo) => (
                  <div key={photo.id}>
                    <div className="border-2 border-black overflow-hidden aspect-square">
                      <img
                        src={photo.url}
                        alt={photo.caption || 'Gallery photo'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {photo.caption && (
                      <p className="font-mono text-xs text-gray-600 mt-1 truncate">
                        {photo.caption}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 3. Vibe Check */}
        {viewedProfile?.vibeCheck && (
          <div className="border-2 border-black bg-white">
            <div className="border-b-2 border-black px-6 py-3">
              <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                VIBE CHECK
              </h2>
            </div>
            <div className="p-6">
              <p className="font-mono text-sm text-gray-800 leading-relaxed">
                {viewedProfile.vibeCheck}
              </p>
            </div>
          </div>
        )}

        {/* 4. Social */}
        {viewedProfile?.socialLink && (
          <div className="border-2 border-black bg-white">
            <div className="border-b-2 border-black px-6 py-3">
              <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                SOCIAL
              </h2>
            </div>
            <div className="p-6">
              <a
                href={`https://instagram.com/${viewedProfile.socialLink.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-sm text-black hover:underline"
              >
                @{viewedProfile.socialLink.replace('@', '')}
              </a>
            </div>
          </div>
        )}

        {/* 5. The Basics */}
        {hasBasics && (
          <div className="border-2 border-black bg-white">
            <div className="border-b-2 border-black px-6 py-3">
              <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                THE BASICS
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {genderDisplay && (
                  <div>
                    <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                      GENDER
                    </h3>
                    <p className="font-mono text-sm text-black">{genderDisplay}</p>
                  </div>
                )}
                {drinksDisplay && (
                  <div>
                    <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                      DRINKS ALCOHOL
                    </h3>
                    <p className="font-mono text-sm text-black">{drinksDisplay}</p>
                  </div>
                )}
                {substancesDisplay && (
                  <div>
                    <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                      PARTY SUBSTANCES
                    </h3>
                    <p className="font-mono text-sm text-black">{substancesDisplay}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 6. Down For (Gathering Types) */}
        {gatheringTypes.length > 0 && (
          <div className="border-2 border-black bg-white">
            <div className="border-b-2 border-black px-6 py-3">
              <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                DOWN FOR
              </h2>
            </div>
            <div className="p-6">
              <div className="flex flex-wrap gap-2">
                {gatheringTypes.map((type, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 border-2 border-black font-mono text-xs uppercase tracking-wider text-black bg-gray-50"
                  >
                    {type}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 7. What You Bring (Extended Profile) */}
        {hasExtended && (
          <div className="border-2 border-black bg-white">
            <div className="border-b-2 border-black px-6 py-3">
              <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                WHAT YOU BRING
              </h2>
            </div>
            <div className="p-6 space-y-5">
              {/* Contribution Tags */}
              {contributionTags.length > 0 && (
                <div>
                  <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                    CONTRIBUTIONS
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {contributionTags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 border-2 border-black font-mono text-xs uppercase tracking-wider text-black bg-gray-50"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Signature Contribution */}
              {signatureContribution && (
                <div>
                  <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    SIGNATURE CONTRIBUTION
                  </h3>
                  <p className="font-mono text-sm text-black">{signatureContribution}</p>
                </div>
              )}

              {/* Likely To */}
              {likelyTo.length > 0 && (
                <div>
                  <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                    MORE LIKELY TO
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {likelyTo.map((item, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 border-2 border-black font-mono text-xs uppercase tracking-wider text-black bg-gray-50"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Music That Moves You */}
              {musicThatMovesYou && (
                <div>
                  <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    MUSIC THAT MOVES YOU
                  </h3>
                  <p className="font-mono text-sm text-black">{musicThatMovesYou}</p>
                </div>
              )}

              {/* Arrival Time */}
              {arrivalTime && (
                <div>
                  <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    ARRIVAL TIME
                  </h3>
                  <p className="font-mono text-sm text-black">{getArrivalLabel(arrivalTime)}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 8. Behavioral Prompts */}
        {answeredPrompts.length > 0 && (
          <div className="border-2 border-black bg-white">
            <div className="border-b-2 border-black px-6 py-3">
              <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                BEHAVIORAL PROMPTS
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {answeredPrompts.map((prompt) => (
                <div key={prompt.key} className="border-l-2 border-black pl-4">
                  <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    {prompt.label}
                  </h3>
                  <p className="font-mono text-sm text-black">
                    {behavioralPrompts[prompt.key]}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 9. Application Context */}
        {application && (
          <div className="border-2 border-black">
            <div className="border-b-2 border-black px-6 py-3">
              <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                APPLICATION
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {application.message && (
                <div>
                  <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                    MESSAGE TO HOST
                  </h3>
                  <p className="font-mono text-sm text-gray-800 italic border-l-2 border-black pl-4">
                    "{application.message}"
                  </p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4 pt-2">
                <div>
                  <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    PLEDGE
                  </h3>
                  <p className="font-mono text-lg font-bold text-black">
                    P${(application.pledgeAmountCents / 100).toFixed(2)}
                  </p>
                </div>
                <div>
                  <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    STATUS
                  </h3>
                  <span className={`inline-block px-2 py-1 font-mono text-xs font-semibold uppercase tracking-wider border ${
                    application.status === 'accepted' ? 'bg-green-100 text-green-800 border-green-500' :
                    application.status === 'pending' ? 'bg-yellow-100 text-yellow-800 border-yellow-500' :
                    application.status === 'declined' ? 'bg-red-100 text-red-800 border-red-500' :
                    application.status === 'paid' ? 'bg-blue-100 text-blue-800 border-blue-500' :
                    'bg-gray-100 text-gray-800 border-gray-500'
                  }`}>
                    {application.status}
                  </span>
                </div>
                <div>
                  <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    APPLIED
                  </h3>
                  <p className="font-mono text-sm text-black">
                    {new Date(application.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
